import { useState } from 'react';
import { routingQueryProblem, useRouting, useRoutingPermitted, type RoutingRow } from '../../api/contacts.ts';
import { LoadFailure } from '../LoadFailure.tsx';
import { StatusTag } from '../StatusTag.tsx';

/**
 * WHO'S CALLING? (Arthur, 16 October). The receptionist routes calls for the whole agency, so she may
 * search every contact, family included, by name, phone number or email, and see whose contact they are:
 * the client's name, reference and status, both case managers and anyone covering. Nothing else of the
 * client: no address, no notes, no record to open. A building's people show the building, never which
 * clients live there; a property's people show the client, never the address.
 *
 * It runs on a press, never as she types, because every search is recorded against the person asking,
 * with any number or email withheld from the record.
 */
export function WhosCalling() {
  const permitted = useRoutingPermitted();
  const [text, setText] = useState('');
  const [asked, setAsked] = useState<string | null>(null);
  const q = useRouting(asked);
  if (permitted.data?.permitted !== true) return null;
  const problem = text.trim() ? routingQueryProblem(text) : null;
  const ask = () => { if (text.trim() && !problem) setAsked(text.trim()); };
  return (
    <section className="mt-8">
      <h2 className="title-bar">Who’s calling?</h2>
      <p className="mt-1 text-[0.9375rem] text-neutral-600">
        Type the caller’s name, phone number or email, then search. You’ll see who they are, whose contact they are,
        and the case managers to put the call through to.
      </p>
      <form className="mt-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); ask(); }}>
        <label htmlFor="whos-calling" className="sr-only">The caller’s name, phone number or email</label>
        <input id="whos-calling" type="search" autoComplete="off" value={text} onChange={(e) => setText(e.target.value)}
               placeholder="Name, phone or email"
               className="min-h-[52px] w-full rounded-lg border border-neutral-300 bg-white px-3 text-base text-neutral-900" />
        <button type="submit" disabled={!text.trim() || problem !== null}
                className="min-h-[52px] shrink-0 rounded-lg bg-neutral-900 px-4 font-semibold text-white disabled:bg-neutral-200 disabled:text-neutral-600">
          Search
        </button>
      </form>
      {problem && <p className="mt-1 text-sm text-neutral-600">{problem}</p>}
      {asked !== null && (
        <div className="mt-3 overflow-hidden rounded-lg border border-neutral-200 bg-white">
          {q.isError ? (
            <LoadFailure what="The search" verb="could not be run" error={q.error} forbiddenIsSession={false}
                         onRetry={() => void q.refetch()} retrying={q.isFetching} />
          ) : !q.data ? (
            <p className="px-3 py-3 text-neutral-500">Searching…</p>
          ) : q.data.contacts.length === 0 ? (
            <p className="px-3 py-3 text-neutral-600">Nobody on record matches. Ask who they are calling about, and use the lookup above.</p>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {q.data.contacts.map((r) => <Caller key={r.link_id} r={r} />)}
            </ul>
          )}
          {q.data?.more && <p className="px-3 pb-2 text-sm text-neutral-500">More than ten match. Type more of the name, or the whole number.</p>}
        </div>
      )}
    </section>
  );
}

function Caller({ r }: { r: RoutingRow }) {
  const role = r.relationship ? `${r.relationship} · ${r.role_label}` : r.role_label;
  const holders = [
    r.case_manager ? `Case manager: ${r.case_manager}` : r.client_ref ? 'No Social Work case manager' : null,
    r.finance_case_manager ? `Finance: ${r.finance_case_manager}` : null,
    r.covering ? `Covering: ${r.covering}` : null,
  ].filter(Boolean).join(' · ');
  return (
    <li className="px-3 py-2">
      <p className="font-semibold text-neutral-900">
        {r.contact_name ?? 'Name not recorded'}
        {r.do_not_contact && (
          <span className="ml-2 rounded border border-warning-edge bg-warning-bg px-1.5 py-0.5 text-xs font-semibold text-warning-fg">Do not contact</span>
        )}
      </p>
      <p className="text-sm text-neutral-700">{role}</p>
      <p className="text-sm text-neutral-700">
        {[r.phone_primary, r.phone_secondary].filter(Boolean).map((p, i) => (
          <span key={p}>{i > 0 && ' · '}<a href={`tel:${p}`} className="inline-flex min-h-[44px] items-center underline">{p}</a></span>
        ))}
        {(r.phone_primary || r.phone_secondary) && (r.email || r.email_secondary) && ' · '}
        {[r.email, r.email_secondary].filter(Boolean).join(' · ')}
      </p>
      {r.place_kind === 'facility' ? (
        <p className="mt-1 text-sm text-neutral-800">At {r.facility_name ?? 'a building'}</p>
      ) : r.client_ref ? (
        <div className="mt-1 text-sm">
          <p className="flex items-baseline gap-2 text-neutral-900">
            <span className="font-semibold">{r.client_name}</span>
            <StatusTag status={r.legal_status} />
            <span className="ml-auto font-mono text-neutral-500 tabular-nums">{r.client_ref}</span>
          </p>
          <p className="text-neutral-700">{holders}{r.place_kind === 'property' ? ' · about a property' : ''}</p>
        </div>
      ) : null}
    </li>
  );
}
